import { Controller } from "@hotwired/stimulus"
import { prefersReducedMotion, replayAnimation } from "../lib/motion"
import { lockScroll, unlockScroll } from "../lib/scroll_lock"

const HOLD_MS = 250

// Help::HowtoStoryComponent: a story of clay scenes. Each scene's bar fills over sceneMs, then the next scene opens;
// the last one stays with the 立即開始 link. Tap the left third to go back, elsewhere to go on; press and hold to
// pause (bar and timer). Reduced motion: no auto-advance, taps only.
export default class extends Controller {
  static targets = ["scene", "bar"]
  static values = { sceneMs: Number }

  connect() {
    // Esc / system back close the <dialog> without close(): release the page there.
    this.onClosed = () => { this.stop(); unlockScroll(this) }
    this.element.addEventListener("close", this.onClosed)
  }

  open() {
    if (!this.element.open) this.element.showModal()
    lockScroll(this, { dismiss: () => this.close() })
    this.show(0)
  }

  close() {
    this.stop()
    if (this.element.open) this.element.close()
  }

  stop() {
    clearTimeout(this.timer)
    this.timer = null
  }

  show(index) {
    this.stop()
    this.index = Math.max(0, Math.min(index, this.sceneTargets.length - 1))
    this.sceneTargets.forEach((scene, i) => scene.classList.toggle("is-active", i === this.index))
    this.barTargets.forEach((bar, i) => {
      bar.classList.toggle("is-done", i < this.index)
      bar.classList.remove("is-active", "is-paused")
      if (i === this.index) replayAnimation(bar, "is-active")
    })
    this.remaining = this.sceneMsValue
    this.schedule()
  }

  schedule() {
    if (prefersReducedMotion() || this.index >= this.sceneTargets.length - 1) return
    this.startedAt = performance.now()
    this.timer = setTimeout(() => this.show(this.index + 1), this.remaining)
  }

  hold(event) {
    if (event.target.closest("a, button")) return
    this.pressedAt = performance.now()
    this.pressX = event.clientX
    if (this.timer) {
      this.stop()
      this.remaining -= performance.now() - this.startedAt
    }
    this.barTargets[this.index]?.classList.add("is-paused")
  }

  release(event) {
    if (this.pressedAt === undefined) return
    const held = performance.now() - this.pressedAt
    this.pressedAt = undefined
    this.barTargets[this.index]?.classList.remove("is-paused")

    if (event.type === "pointerup" && held < HOLD_MS) {
      const { left, width } = this.element.getBoundingClientRect()
      this.show(this.pressX - left < width / 3 ? this.index - 1 : this.index + 1)
    } else {
      this.schedule()
    }
  }

  // Holding on a scene would otherwise open the system image / link menu (and cancel the hold).
  suppressMenu(event) {
    event.preventDefault()
  }

  disconnect() {
    this.element.removeEventListener("close", this.onClosed)
    this.stop()
    unlockScroll(this)
  }
}
